import React from 'react';
import { CheckCircle2, Dna, Info, Lock, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DNA_UPLOAD_RESULT_DISCLAIMER,
  DNA_UPLOAD_RESULT_FUTURE_MATCHING,
  DNA_UPLOAD_RESULT_LOW_MARKER_NOTE,
  DNA_UPLOAD_RESULT_PRIVACY,
  DNA_UPLOAD_RESULT_STATUS,
  DNA_UPLOAD_RESULT_TITLE,
  DNA_UPLOAD_RESULT_WHAT_NEXT,
} from '../../utils/dnaUploadCopy';
import {
  getDnaProviderDisplayLabel,
  shouldShowLowMarkerNote,
} from '../../utils/dnaUploadDisplay';

function formatFileType(filename, markerData) {
  if (filename && filename.includes('.')) {
    return filename.slice(filename.lastIndexOf('.')).toLowerCase();
  }
  const fmt = markerData?.file_format;
  if (fmt === 'tsv' || fmt === 'txt') return '.txt';
  if (fmt === 'csv') return '.csv';
  return '—';
}

function formatUploadDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function MetaRow({ label, value }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-border/30 pb-3 last:border-0 last:pb-0 sm:grid-cols-[9.5rem_1fr] sm:items-start sm:gap-4">
      <dt className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">{label}</dt>
      <dd className="text-sm leading-snug text-foreground break-words sm:text-right">{value ?? '—'}</dd>
    </div>
  );
}

/**
 * Private post-upload status card — genealogy storage only, not medical analysis.
 */
export function DnaUploadResultCard({
  profile,
  onDismiss,
  onManageProfile,
  onManagePrivacy,
  onUploadAnother,
  className = '',
}) {
  if (!profile) return null;

  const markerData = profile.dna_marker_data || {};
  const markerCount =
    markerData.total_markers != null ? Number(markerData.total_markers) : null;
  const provider = getDnaProviderDisplayLabel(profile);
  const chromosomes =
    markerData.chromosomes_detected != null
      ? markerData.chromosomes_detected
      : markerData.has_chromosome_data
        ? 'Detected'
        : '—';
  const matchingLabel = profile.matching_enabled
    ? 'Enabled (early access)'
    : 'Early access / Not enabled yet';
  const showLowMarkerNote = shouldShowLowMarkerNote(markerCount);

  return (
    <article
      className={`rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 via-card to-card p-5 sm:p-6 shadow-sm ${className}`}
      data-testid="dna-upload-result-card"
    >
      <header className="flex items-start gap-3 border-b border-border/50 pb-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <CheckCircle2 className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-serif text-lg font-medium text-foreground">{DNA_UPLOAD_RESULT_TITLE}</h3>
            <span className="rounded-full border border-amber-600/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:text-amber-200">
              Beta
            </span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{DNA_UPLOAD_RESULT_STATUS}</p>
        </div>
      </header>

      <dl className="mt-4 rounded-lg border border-border/40 bg-secondary/20 px-4 py-3.5">
        <MetaRow label="Provider" value={provider} />
        <MetaRow label="File name" value={profile.original_filename || '—'} />
        <MetaRow label="File type" value={formatFileType(profile.original_filename, markerData)} />
        <MetaRow label="Upload date" value={formatUploadDate(profile.uploaded_at)} />
        <MetaRow
          label="Marker count"
          value={markerCount != null ? markerCount.toLocaleString() : '—'}
        />
        <MetaRow label="Chromosomes detected" value={chromosomes} />
        <MetaRow label="Status" value="Stored privately" />
        <MetaRow label="Matching" value={matchingLabel} />
      </dl>

      {showLowMarkerNote ? (
        <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-sky-700/15 bg-sky-500/5 px-3 py-2.5">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-sky-700 dark:text-sky-400" aria-hidden />
          <p className="text-[11px] leading-relaxed text-muted-foreground">{DNA_UPLOAD_RESULT_LOW_MARKER_NOTE}</p>
        </div>
      ) : null}

      <section className="mt-4 rounded-lg border border-border/50 bg-card/80 px-3 py-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          What happens next?
        </p>
        <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{DNA_UPLOAD_RESULT_WHAT_NEXT}</p>
      </section>

      <div className="mt-4 space-y-3">
        <div className="flex items-start gap-2.5 rounded-lg border border-border/50 bg-card/80 px-3 py-2.5">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
          <p className="text-[11px] leading-relaxed text-muted-foreground">{DNA_UPLOAD_RESULT_PRIVACY}</p>
        </div>
        <div className="flex items-start gap-2.5 rounded-lg border border-border/40 px-3 py-2.5">
          <Dna className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <p className="text-[11px] leading-relaxed text-muted-foreground">{DNA_UPLOAD_RESULT_FUTURE_MATCHING}</p>
        </div>
        <div className="flex items-start gap-2.5 rounded-lg border border-amber-700/20 bg-amber-500/5 px-3 py-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden />
          <p className="text-[11px] leading-relaxed text-muted-foreground">{DNA_UPLOAD_RESULT_DISCLAIMER}</p>
        </div>
      </div>

      <footer className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
        {typeof onManageProfile === 'function' ? (
          <Button type="button" variant="default" size="sm" className="rounded-full" onClick={onManageProfile}>
            Manage DNA profile
          </Button>
        ) : null}
        {typeof onManagePrivacy === 'function' ? (
          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={onManagePrivacy}>
            Manage privacy
          </Button>
        ) : null}
        {typeof onUploadAnother === 'function' ? (
          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={onUploadAnother}>
            Upload another file
          </Button>
        ) : null}
        {typeof onDismiss === 'function' ? (
          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={onDismiss}>
            Done
          </Button>
        ) : null}
      </footer>
    </article>
  );
}

export default DnaUploadResultCard;
